import React, { FunctionComponent, useCallback, useState, useRef, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  Menu,
  MenuItem,
  Grid,
  Typography,
  Button,
  CircularProgress,
  Avatar,
  makeStyles,
  IconButton,
  useMediaQuery,
  TButtonProps,
  ArrowDropDownRoundedIcon,
} from '@rbx/ui';
import { Thumbnail2d, ThumbnailTypes } from '@rbx/thumbnails';
import { User } from './types';
import AuthenticationStatusDrawer from './AuthenticationStatusDrawer';
import NavigationTranslate from '../../hooks/NavigationTranslate';
import { getCreatorHubBasePath } from '../../utils/getBasePaths';
import useNavigationConfigs from '../../hooks/useNavigationConfigs';
import { clickNavSettingsEventModel } from '../../event/eventConstants';
import { drawerId } from '../constants/navigationConstants';
import { TSendEvent, useEventLogger } from '../../providers/EventProvider';

type TStyleProps = {
  minWidth?: number;
};

const useAuthenticationStatusStyles = makeStyles<TStyleProps>()((theme, { minWidth }) => {
  return {
    root: {
      whiteSpace: 'nowrap',
      display: 'flex',
      alignItems: 'center',
    },
    buttonStyle: {
      '&:focus-visible': {
        outline: `${theme.palette.content.standard} auto`,
        outlineOffset: '-8px',
      },
      [theme.breakpoints.down('Medium')]: {
        paddingTop: '2px',
        paddingBottom: '2px',
      },
    },
    menuList: {
      pointerEvents: 'auto',
      minWidth: Math.max(minWidth || 200, 200),
      width: '100%',
    },
    userName: {
      marginLeft: 10,
      textTransform: 'none',
    },
  };
});

type TAuthenticationStatusProps = {
  loading: boolean;
  user: User | null;
  avatarOnly?: boolean;
  onLogout: () => void;
  onLogin: () => void;
  desktopDropdownContent?: ReactNode;
  mobileDropdownContent?: ReactNode;
  buttonProps?: Omit<TButtonProps, 'onClick'>;
  sendEvent: TSendEvent;
  settingsHref: string;
};

const AuthenticationStatus: FunctionComponent<TAuthenticationStatusProps> = ({
  loading,
  user,
  avatarOnly = false,
  desktopDropdownContent,
  mobileDropdownContent,
  onLogout,
  onLogin,
  buttonProps,
  sendEvent,
  settingsHref,
}) => {
  const anchorElementRef = useRef<HTMLButtonElement | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isLastMovementKeyboard, setIsLastMovementKeyboard] = useState(false);
  const [accountSideBarOpen, setAccountSideBarOpen] = useState(false);
  const isCollapsed = useMediaQuery((theme) => theme.breakpoints.down('Large'));
  const buttonId = 'top-navigation-authentication-status-button';
  const menuId = 'top-navigation-authentication-status-menu';

  const {
    classes: { root, buttonStyle, menuList, userName },
  } = useAuthenticationStatusStyles({
    minWidth: anchorElementRef?.current?.offsetWidth,
  });

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const onClickSettings = useCallback(() => {
    sendEvent(clickNavSettingsEventModel);
    setTimeout(() => {
      closeMenu();
      window.open(settingsHref, '_self');
    }, 100);
  }, [closeMenu, sendEvent, settingsHref]);

  const onClickButton = useCallback(() => {
    setIsMenuOpen((prev) => !prev);
  }, []);

  const isKeyEscapeOrTab = (event: React.KeyboardEvent) => {
    return event.key === 'Escape' || event.key === 'Tab';
  };

  const onKeyDownButton = useCallback(
    (event: React.KeyboardEvent) => {
      setIsLastMovementKeyboard(true);
      if (isKeyEscapeOrTab(event)) {
        closeMenu();
      }
    },
    [closeMenu],
  );

  const onKeyDownMenu = useCallback(
    (event: React.KeyboardEvent) => {
      setIsLastMovementKeyboard(true);
      if (isKeyEscapeOrTab(event)) {
        closeMenu();
      }
    },
    [closeMenu],
  );

  if (loading) {
    return (
      <div className={root}>
        <CircularProgress color='secondary' />
      </div>
    );
  }

  if (user == null) {
    return (
      <div className={root}>
        <Button
          variant='text'
          color='secondary'
          onClick={onLogin}
          className={buttonStyle}
          disableFocusRipple
          {...buttonProps}>
          <NavigationTranslate content='Action.LogIn' />
        </Button>
      </div>
    );
  }

  const avatar = (
    <Avatar alt='avatar'>
      <Thumbnail2d
        targetId={user.id ?? 0}
        type={ThumbnailTypes.avatarHeadshot}
        alt={user.name ?? ''}
      />
    </Avatar>
  );

  if (isCollapsed && !avatarOnly) {
    const drawerContainer = document.getElementById(drawerId);

    return (
      <div className={root}>
        <IconButton
          onClick={() => {
            setAccountSideBarOpen(!accountSideBarOpen);
          }}
          aria-label='avatar'
          size='large'>
          {avatar}
        </IconButton>
        {drawerContainer
          ? createPortal(
              <AuthenticationStatusDrawer
                open={accountSideBarOpen}
                user={user}
                onLogout={onLogout}
                onSettingsClick={onClickSettings}
                onClickClose={() => {
                  setAccountSideBarOpen(false);
                }}
                mobileDropdownContent={mobileDropdownContent}
              />,
              drawerContainer,
            )
          : null}
      </div>
    );
  }

  return (
    <div className={root}>
      <Button
        onClick={onClickButton}
        ref={anchorElementRef}
        endIcon={isCollapsed || avatarOnly ? null : <ArrowDropDownRoundedIcon color='secondary' />}
        onKeyDown={onKeyDownButton}
        className={buttonStyle}
        disableFocusRipple
        aria-expanded={isMenuOpen}
        aria-haspopup='true'
        aria-controls={isMenuOpen ? menuId : undefined}>
        <Grid container direction='row' alignItems='center' wrap='nowrap'>
          <Grid item>{avatar}</Grid>
          {isCollapsed || avatarOnly ? null : (
            <Grid item className={userName}>
              <Typography variant='largeLabel2' color='secondary'>
                {user.name}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Button>
      <Menu
        MenuListProps={{
          classes: { root: menuList },
          onKeyDown: onKeyDownMenu,
        }}
        anchorEl={anchorElementRef.current}
        id={menuId}
        open={Boolean(anchorElementRef.current) && isMenuOpen}
        onClose={closeMenu}
        disableAutoFocusItem={!isLastMovementKeyboard}
        aria-labelledby={buttonId}
        role='menu'
        variant='menu'>
        {desktopDropdownContent}
        <MenuItem role='menuitem' component='a' onClick={onClickSettings} href={settingsHref}>
          <Typography>
            <NavigationTranslate content='Heading.Settings' />
          </Typography>
        </MenuItem>
        <MenuItem role='menuitem' onClick={onLogout}>
          <Typography>
            <NavigationTranslate content='Action.LogOut' />
          </Typography>
        </MenuItem>
      </Menu>
    </div>
  );
};

type AuthenticationStatusV1Props = Omit<
  TAuthenticationStatusProps,
  'sendEvent' | 'settingsHref' | 'onlyMenu'
>;

const AuthenticationStatusV1: FunctionComponent<AuthenticationStatusV1Props> = (props) => {
  const { sendEvent, target, environment } = useNavigationConfigs();
  const settingsHref = `${getCreatorHubBasePath(target, environment)}/settings/preferences`;
  return <AuthenticationStatus settingsHref={settingsHref} sendEvent={sendEvent} {...props} />;
};

type AuthenticationStatusV2Props = Omit<TAuthenticationStatusProps, 'sendEvent' | 'mobileMenu'>;
export const AuthenticationStatusV2: FunctionComponent<AuthenticationStatusV2Props> = (props) => {
  const sendEvent = useEventLogger();
  return <AuthenticationStatus sendEvent={sendEvent} {...props} />;
};

export default AuthenticationStatusV1;
